import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { find, findIndex, get } from 'lodash';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { TrackingHandler } from 'client/tracking/handler';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { EngineTypeQuizMap } from 'client/tracking/maps/common/engine-type-quiz';
import { TrackingEvent } from 'client/tracking/event';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities, CmsModel, CmsPaths } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import {
  CAR_NEWS_CATEGORY_EDITORIAL_PATH,
  ENTRIES_TO_AVOID,
  RESEARCH_CATEGORY_EDITORIAL_PATH,
  RESEARCH_CUSTOM_ADS_PATH,
  RESEARCH_LINK_WIDGET_EDITORIAL_PATH,
} from 'site-modules/shared/constants/editorial/research-article';
import { CAR_PICTURES, SELL_CAR } from 'site-modules/editorial/constants/slugs';
import { IMAGE_SIZES } from 'site-modules/shared/constants/editorial/editorial';
import { EDITORIAL_HEADER_SRC_INLINE } from 'site-modules/shared/constants/editorial/editorial-header';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { getResearchCategoryName } from 'site-modules/editorial/utils/research-article';
import { getResearchArticleBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { getMoreInCategoriesLinks } from 'site-modules/editorial/utils/entry-converter';
import { renderEntries } from 'site-modules/editorial/utils/entries-renderer';
import { getTitleOverride } from 'site-modules/editorial/pages/research-article/seo';
import { entriesTypes, getFilteredRegExpArr } from 'site-modules/editorial/utils/entries-types';
import { isSuitableEntry } from 'site-modules/editorial/utils/is-suitable-entry';
import { insertContentAd } from 'site-modules/editorial/utils/insert-content-ad';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { getResearchArticleIndexPath } from 'site-modules/editorial/utils/content-path';
import { isTitleEvenCount } from 'site-modules/editorial/utils/homepage-ads-title-count';
import { isCarNews as isCarNewsCategory } from 'client/site-modules/editorial/utils/car-news';
import { isEvArticle } from 'site-modules/editorial/utils/ev';
import { scrollToHash } from 'client/utils/scroll';
import { getHeroPhoto } from 'client/site-modules/editorial/utils/hero-photo';
import { transformAllAuthorsContent } from 'site-modules/shared/utils/editorial/author';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { SeoLinks } from 'site-modules/shared/components/seo-links/seo-links';
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';
import { ResearchSubnav } from 'site-modules/shared/components/editorial/research-subnav/research-subnav';
import { ArticleMenu } from 'site-modules/shared/components/editorial/article-menu/article-menu';
import { ArticleMetadata } from 'site-modules/shared/components/editorial/article-metadata/article-metadata';
import { AllAuthorsMetadata } from 'site-modules/shared/components/editorial/author-metadata/all-authors-metadata';
import { Share } from 'site-modules/shared/components/share/share';
import { MoreInCategoryLink } from 'site-modules/editorial/components/article-no-hero/more-in-category-link/more-in-category-link';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { SuggestedVehiclePricingCarouselContainer } from 'site-modules/shared/components/suggested-vehicle-pricing-carousel/suggested-vehicle-pricing-carousel-container';
import { HomeMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/home-marquee';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { NewsWidget } from 'site-modules/shared/components/news-widget/news-widget';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { EngineTypeQuiz } from 'site-modules/shared/components/engine-type-quiz/engine-type-quiz';
import { ExcoVideoWrapperAsync } from 'site-modules/shared/components/exco-video/exco-video-wrapper-async';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import './research-article.scss';

const HERO_SIZE = { true: IMAGE_SIZES.sm_md, false: IMAGE_SIZES.lg };
const HERO_PADDING = { true: 160, false: 110 };
export const ARTICLE_TITLE_WHAT_NEW_CAR_FEE_SHOULD_YOU_PAY = 'What New Car Fees Should You Pay?';

/**
 * Splits article with one html entry to separate entries for each paragraph to add ads in the text. Makes no changes
 * for modular articles.
 * @param {Array} entriesToRender
 */
function splitNonModularEntry(entriesToRender) {
  const entryToSplit = find(entriesToRender, { id: '0' });

  if (entryToSplit) {
    entriesToRender.splice(
      findIndex(entriesToRender, ({ id }) => id === '0'),
      1
    );
    entryToSplit.content
      .split('</p>')
      .map(entryContent =>
        entriesToRender.push({ id: `html`, content: `${entryContent}</p>`, legacy: true, className: 'mb-0' })
      );

    entriesToRender.splice(entriesToRender.length - 1, 1);
  }
}

function ResearchArticleUI({
  content,
  allAuthors,
  slug,
  splat,
  articleId,
  researchCategories,
  carNewsCategory,
  linkWidgetContent,
  isMobile,
  location,
}) {
  useEffect(() => {
    TrackingHandler.useMap({
      ...EditorialMap,
      ...EngineTypeQuizMap,
      appraise_your_car: TrackingEvent.actionCompleted(
        TrackingConstant.ACTION_TOOL_SELECT,
        TrackingConstant.APPRAISE_YOUR_CAR
      ),
    });

    const hash = get(location, 'hash', '');
    scrollToHash(hash, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { title, subtitle } = content;
  const isLegacy = !/^\/research\//.test(content.id);
  const isCarNews = isCarNewsCategory(slug);
  const isEvArticlePage = isEvArticle({ slug, categories: content.categories });
  const isEvenCount = isTitleEvenCount(title, subtitle);
  const categoryName = isCarNews ? splat : getResearchCategoryName(slug);
  const categoryContent = isCarNews
    ? carNewsCategory.child(splat)
    : researchCategories.child(`category-${categoryName}`);
  const canonicalURL = getCanonical(content);

  // Subnav data
  const subnavContent = researchCategories.child('subnav');
  const subnavHeading = subnavContent.metadata('subnavTitle').value();
  const subnavLinks = subnavContent.links();

  // Related articles data
  const moreInContent = categoryContent.child(`right-rail-links-${categoryName}`);
  const articleMenuLinks = getMoreInCategoriesLinks(canonicalURL, moreInContent.children());
  const articleMenuTitle = moreInContent.metadata('moduleHeader').value();
  const articleMenuMoreLink = {
    href: moreInContent.metadata('seeAllLink').value(),
    name: moreInContent.metadata('seeAllTitle').value(),
    accessibleText: moreInContent.metadata('seeAllAccessibleText').value(),
  };

  // Article content ads config
  const CONTENT_ADS_CONFIG = [
    {
      id: 'mrect-ad-unit',
      contentEntriesBeforeAd: [5],
      priorityAtTheBottom: 1,
      component: (
        <MrectNative
          wrapperClass="mb-2"
          position={'1'}
          renderWhenViewable={isMobile}
          verticalOffset="100%"
          mobileBreakpoints={{ xs: true, sm: true, md: true }}
          refreshable={false}
          mobileOnly
          useMinHeight
        />
      ),
    },
    {
      id: 'ad-unit-spot',
      contentEntriesBeforeAd: [2, 8],
      priorityAtTheBottom: 0,
      componentConfig: {
        adName: 'spot',
        mobileAdName: 'mmrect',
        positions: ['1', '2'],
        otherAdUnitProps: {
          wiredBreakpoints: { md: true },
          mobileBreakpoints: { xs: true, sm: true, md: true },
          renderWhenViewable: isMobile,
          verticalOffset: '100%',
          refreshable: false,
          wrapperClass: 'hidden-lg-up',
          customSizes: [
            [1, 1],
            [320, 50],
            [300, 90],
            [300, 180],
            [300, 250],
          ],
        },
      },
    },
    {
      id: 'ad-unit-spot',
      contentEntriesBeforeAd: [12, 16],
      priorityAtTheBottom: 0,
      componentConfig: {
        adName: 'spot',
        mobileAdName: 'mmrect',
        positions: ['3', '4'],
        otherAdUnitProps: {
          wiredBreakpoints: { md: true },
          mobileBreakpoints: { xs: true, sm: true, md: true },
          renderWhenViewable: isMobile,
          verticalOffset: '100%',
          refreshable: false,
          wrapperClass: 'hidden-lg-up',
          customSizes: [
            [1, 1],
            [320, 50],
            [300, 90],
            [300, 180],
            [300, 250],
          ],
        },
      },
    },
  ];

  // Article content data
  const arrOfEntriesUsedOnPage = getFilteredRegExpArr(entriesTypes, ENTRIES_TO_AVOID);
  const contentChildren = content.children();
  splitNonModularEntry(contentChildren);
  const contentEntries = insertContentAd({
    entries: contentChildren
      .filter(entry => isSuitableEntry(entry, arrOfEntriesUsedOnPage, isLegacy))
      .map(entry =>
        isLegacy
          ? {
              ...entry,
              id: 'html',
              legacy: true,
            }
          : entry
      ),
    adsConfig: CONTENT_ADS_CONFIG,
    isMobile,
  });

  const hasEvQuizEntry = contentEntries.some(({ id }) => entriesTypes.evQuiz.test(id));
  const isCarPicturesSlug = slug === CAR_PICTURES;

  const heroPhoto = getHeroPhoto(content);

  const articleTitle = getTitleOverride(content) || title;

  const hasHowToSchema = content.metadata('hasHowToSchema').value('') === 'true';

  const excoTestPageArticleTitles = [ARTICLE_TITLE_WHAT_NEW_CAR_FEE_SHOULD_YOU_PAY];
  const isExcoTestPage = excoTestPageArticleTitles.includes(articleTitle);

  return (
    <main
      id="main-content"
      className="research-article article-no-hero mb-3_5 pos-r"
      {...(hasHowToSchema && { itemScope: true, itemType: 'https://schema.org/HowTo' })}
      data-ad-zone={articleId}
    >
      <div className={classnames('pos-r', { 'mb-1': !heroPhoto })}>
        <img
          src={EDITORIAL_HEADER_SRC_INLINE[isMobile]}
          alt=""
          fetchpriority="high"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: '0',
            width: '100%',
            height: '100%',
          }}
        />
        <Container style={heroPhoto ? { paddingBottom: `${HERO_PADDING[isMobile]}px` } : {}}>
          <Row>
            <Col xs={12} lg={8} className={classnames({ 'pb-1 pe-lg-1': !heroPhoto })}>
              <div className="text-shadow-contrast-high">
                <Breadcrumbs
                  className="ps-0 text-white mb-1_5 mb-md-2"
                  data={getResearchArticleBreadcrumbs([slug], title)}
                />
                <div className="heading-container mb-1_5 mb-lg-2">
                  <h1
                    className={classnames('text-white fw-bold', {
                      'mb-0-75': !!subtitle,
                      'size-36': !isMobile,
                      'size-24': isMobile,
                    })}
                    {...(hasHowToSchema && { itemProp: 'name' })}
                  >
                    {articleTitle}
                  </h1>
                  {!!subtitle && <h2 className="size-16 text-white fw-medium">{subtitle}</h2>}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Container>
        <Row>
          <Col
            tag="article"
            xs={12}
            lg={8}
            className="pb-1_5 pe-lg-1"
            style={heroPhoto ? { marginTop: `-${HERO_PADDING[isMobile]}px` } : {}}
          >
            {!!heroPhoto && (
              <AspectRatioContainer className="rounded-12 mb-0_75">
                <img src={getFullImageUrl(heroPhoto.href, HERO_SIZE[isMobile])} alt="" decoding="async" />
              </AspectRatioContainer>
            )}
            <ArticleMetadata
              className="mb-2"
              content={content}
              allAuthors={allAuthors}
              creativeId="edm-entry-article-metadata"
              isMobile={isMobile}
            />
            {isMobile && <MoreInCategoryLink categorySlug={slug} className="mb-1" />}
            {isCarPicturesSlug && <ArticlePhoto className="mb-2" photos={content.links()} />}
            <div className={classnames('size-16 mb-0_5', { 'legacy-entries': isLegacy })}>
              <Experiment name="ads-11436" showDefault>
                <Recipe name="ctrl" isDefault />
                <Recipe name="chal">
                  <div style={{ aspectRatio: '16 / 9' }} className="rounded-8 mb-1_5 overflow-hidden">
                    <ExcoVideoWrapperAsync showTestText={false} />
                  </div>
                </Recipe>
              </Experiment>
              {renderEntries({
                entriesToRender: contentEntries,
                withWrapper: true,
                slug,
                categories: get(content, 'categories', []),
                pathname: location.pathname,
                includeAppraisalSellModuleInEntries: true,
                includeTreehousePromo: true,
                options: {
                  isMobile,
                  canonical: canonicalURL,
                  contentId: content.id,
                  entryPath: [],
                  pageTitle: title,
                },
              })}
            </div>

            <footer>
              <div className="text-center text-md-start">
                <Share url={canonicalURL} title={content.title} />
              </div>
              <AllAuthorsMetadata allAuthors={allAuthors} />
            </footer>
            <hr className="mt-1_5 mb-1" />
            <aside>
              {isEvenCount && (
                <ErrorBoundary>
                  <HomeMarquee useMinHeight />
                  <hr className="mt-0 col-12 mb-2 hidden-lg-down" />
                </ErrorBoundary>
              )}
              {!isEvenCount && (
                <ErrorBoundary>
                  <VehicleShowcase
                    content={linkWidgetContent.child('home-vehicle-showcase-v2')}
                    bottomBorderHiddenMdDown
                  />
                </ErrorBoundary>
              )}
            </aside>
          </Col>
          <Col tag="aside" xs={12} lg={4} className="ps-lg-1">
            <hr className="mt-0" />
            <ResearchSubnav className="px-1" heading={subnavHeading} links={subnavLinks} />
            <hr className="mt-0 mb-2" />
            <NativeAd
              adNameMobile="mmrect"
              nativeStyle="none"
              wrapperClass="hidden-lg-up"
              position={'5'}
              mobileBreakpoints={{ xs: true, sm: true, md: true }}
              renderWhenViewable={isMobile}
              verticalOffset="100%"
              refreshable={false}
              useMinHeight
              showLabel
              mobileOnly
            />
            <ArticleMenu
              links={articleMenuLinks}
              title={articleMenuTitle}
              moreLink={articleMenuMoreLink}
              className="mb-2 mb-lg-3 mt-3_5"
              dividerClassName="d-none"
              titleContainerClassName="p-1 bg-gray-lighter"
              titleClassName="heading-5"
              listClassName="bg-gray-extra-light px-1"
              itemsClassName="article-menu-item py-1"
              linksClassName="heading-5 fw-normal"
              moreLinkClassName="heading-5 fw-normal text-primary-darker"
            />
            <div className="hidden-lg-down">
              <NativeAd
                adNameWired="mrect"
                nativeStyle="mediumrectangle"
                position={'1'}
                wiredBreakpoints={{ lg: true, xl: true }}
                renderWhenViewable={false}
                refreshable={false}
                verticalAlign="center"
                useMinHeight
                showLabel
              />
            </div>
            {!isMobile && (
              <div className="separator mrect-wrapper hidden-lg-down my-2">
                <NativeAd
                  adNameWired="mrect"
                  nativeStyle="mediumrectangle"
                  position={'2'}
                  wiredBreakpoints={{ lg: true, xl: true }}
                  renderWhenViewable={false}
                  refreshable={false}
                  verticalAlign="center"
                  useMinHeight
                  showLabel
                />
              </div>
            )}
            <div className="separator native-video-wrapper mt-2">
              {isExcoTestPage ? (
                <FeatureFlag name="enableExco">
                  {enabled => (enabled ? null : <NativeVideoAd useMinHeight />)}
                </FeatureFlag>
              ) : (
                <NativeVideoAd useMinHeight />
              )}
            </div>
            {!isMobile && (
              <div className="separator mrect-wrapper hidden-lg-down my-2">
                <NativeAd
                  adNameWired="mrect"
                  nativeStyle="mediumrectangle"
                  position={'3'}
                  wiredBreakpoints={{ lg: true, xl: true }}
                  renderWhenViewable
                  refreshable={false}
                  verticalAlign="center"
                  useMinHeight
                  showLabel
                />
              </div>
            )}
            <hr className="hidden-lg-up my-2" />
          </Col>
        </Row>
        <aside>
          <MinHeightWrapper
            className="suggested-pricing-wrapper show-separator"
            componentType="SUGGESTED_PRICING_CAROUSEL"
          >
            <SuggestedVehiclePricingCarouselContainer className="px-0" />
          </MinHeightWrapper>
          <hr className="mt-1_5 mb-2" />
          {isCarNews && (
            <Fragment>
              <hr className="mt-1_5 mb-2" />
              <NewsWidget />
              <hr className="mt-1_5 mb-2" />
            </Fragment>
          )}
          {isEvArticlePage && !hasEvQuizEntry && (
            <Fragment>
              <Row>
                <Col xs={12} lg={8}>
                  <EngineTypeQuiz />
                </Col>
              </Row>
              <hr className="mt-1_5 mb-2" />
            </Fragment>
          )}
          {slug === SELL_CAR ? (
            <PageLinksWidget divideSections={false} title="Related information" />
          ) : (
            <SeoLinks
              content={linkWidgetContent.child('home-seo-footer')}
              className="most-popular-vehicles"
              btnClassName="ps-0_5"
            />
          )}
        </aside>
      </Container>
    </main>
  );
}

ResearchArticleUI.propTypes = {
  slug: PropTypes.string.isRequired,
  splat: PropTypes.string.isRequired,
  articleId: PropTypes.string,
  content: CmsEntities.Content.isRequired,
  allAuthors: PropTypes.shape({
    author: CmsEntities.Content,
    coAuthor: CmsEntities.Content,
    editor: CmsEntities.Content,
  }),
  researchCategories: CmsEntities.Content,
  carNewsCategory: CmsEntities.Content,
  linkWidgetContent: CmsEntities.Content,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({}),
};

ResearchArticleUI.defaultProps = {
  splat: '',
  articleId: '',
  allAuthors: {
    author: DEFAULT_CONTENT,
    coAuthor: DEFAULT_CONTENT,
    editor: DEFAULT_CONTENT,
  },
  researchCategories: DEFAULT_CONTENT,
  carNewsCategory: DEFAULT_CONTENT,
  linkWidgetContent: DEFAULT_CONTENT,
  isMobile: false,
  location: {},
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  location: get(state, 'pageContext.location'),
});

export const ResearchArticle = connect(mapStateToProps)(
  connectToModel(ResearchArticleUI, {
    allAuthors: bindToPath(
      ({ slug, articleId, splat }) =>
        CmsPaths.buildAllAuthorsPath({
          contentPath: getResearchArticleIndexPath(slug, articleId, splat),
        }),
      CmsModel,
      transformAllAuthorsContent
    ),
    researchCategories: bindToContent(RESEARCH_CATEGORY_EDITORIAL_PATH),
    carNewsCategory: bindToContent(CAR_NEWS_CATEGORY_EDITORIAL_PATH),
    customAds: bindToContent(RESEARCH_CUSTOM_ADS_PATH),
    linkWidgetContent: bindToContent(RESEARCH_LINK_WIDGET_EDITORIAL_PATH),
  })
);
